<template>
  <div v-if="visibleItems.length > 0" :class="wrapClass">
    <ul>
      <li
        v-for="item in visibleItems"
        :key="item.menuId"
        :class="{ on: isActive(item) }"
      >
        <a
          v-if="isOutLink(item)"
          :href="hrefForMenu(item)"
          target="_blank"
          title="새창"
          rel="noopener noreferrer"
        >
          {{ item.menuNm }}
        </a>
        <a
          v-else
          href="#"
          @click.prevent="navigate(item)"
        >
          {{ item.menuNm }}
        </a>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

defineOptions({ name: 'TabGroup' })

const props = defineProps({
  /** 메뉴 항목 배열 ({ menuId, menuNm, menuType, menuLink, path? }) */
  items: {
    type: Array,
    default: () => []
  },
  /**
   * 탭 유형 (nav_type)
   *  'T' - 일반 탭 (lnb tmn01)
   *  'Y' - 박스형 (lnb tmn03)
   *  'E' - 년도형 (lnb tmn04)
   */
  navType: {
    type: String,
    default: 'T',
    validator: (v) => ['T', 'Y', 'E'].includes(v)
  },
  /** 현재 활성화된 메뉴 ID (외부에서 강제 지정 시) */
  activeId: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['menu-change'])

const route = useRoute()
const router = useRouter()

const clickedMenuId = ref(null)

// 라우트 변경 시 클릭 상태 초기화
watch(() => route.fullPath, () => {
  clickedMenuId.value = null
})

// menuIndctYn='Y'인 항목만 표시 (SubNav와 동일 기준)
const visibleItems = computed(() =>
  (props.items || []).filter(item =>
    item.menuIndctYn === 'Y' || item.menuIndctYn === 'YES'
  )
)

// ---------- 클래스 ----------

const navTypeClassMap = {
  T: 'tmn01',
  Y: 'tmn03',
  E: 'tmn04'
}

const wrapClass = computed(() => {
  return ['lnb', navTypeClassMap[props.navType] || 'tmn01']
})

// ---------- 헬퍼 ----------

const getMenuType = (menu) => String(menu?.menuType || '').toUpperCase()

const isOutLink = (menu) => {
  const t = getMenuType(menu)
  return t === 'LINK' || t === 'OUT_LINK'
}

const normalizeLink = (raw) => {
  const link = String(raw || '').trim()
  if (!link || link === 'dir' || link === 'N') return null
  if (/^https?:\/\//i.test(link)) return link
  return link.startsWith('/') ? link : `/${link}`
}

const findFirstNavigableChild = (menu) => {
  const children = Array.isArray(menu?.children) ? menu.children : []
  for (const child of children) {
    const href = hrefForMenu(child)
    if (href) return child
  }
  return null
}

const hrefForMenu = (menu) => {
  if (!menu) return null
  const menuType = getMenuType(menu)

  if (menuType === 'DIR') {
    const first = findFirstNavigableChild(menu)
    return first ? hrefForMenu(first) : null
  }
  if (menuType === 'CONTENT') {
    const link = String(menu.menuLink || '').trim()
    if (!link || link === 'dir' || link === 'N') return null
    const normalized = link.startsWith('/') ? link : `/${link}`
    return normalized.startsWith('/cms/') ? normalized : `/cms${normalized}`
  }
  return normalizeLink(menu?.path) || normalizeLink(menu?.menuLink)
}

// ---------- 네비게이션 ----------

const navigate = async (menu) => {
  if (!menu) return
  const menuType = getMenuType(menu)

  if (menuType === 'DIR') {
    const first = findFirstNavigableChild(menu)
    if (first) await navigate(first)
    return
  }
  if (menuType === 'CONTENT') {
    const link = String(menu.menuLink || '').trim()
    if (!link || link === 'dir' || link === 'N') return
    const normalized = link.startsWith('/') ? link : `/${link}`
    const href = normalized.startsWith('/cms/') ? normalized : `/cms${normalized}`
    clickedMenuId.value = menu.menuId
    await router.push({ path: href, query: { ...route.query, menuId: menu.menuId } })
    return
  }

  const href = hrefForMenu(menu)
  if (!href) return

  clickedMenuId.value = menu.menuId

  if (/^https?:\/\//i.test(href)) {
    window.open(href, '_blank', 'noopener,noreferrer')
    return
  }

  emit('menu-change', menu.menuId)
  await router.push({ path: href, query: { ...route.query, menuId: menu.menuId } })
}

// ---------- 활성 상태 ----------

const stripSlash = (p = '') => String(p).replace(/\/$/, '')

const getPathname = (raw) => {
  if (!raw) return ''
  try {
    return stripSlash(new URL(raw, window.location.origin).pathname)
  } catch {
    return stripSlash(String(raw))
  }
}

const isActive = (item) => {
  if (props.activeId && props.activeId === item.menuId) return true
  if (clickedMenuId.value === item.menuId) return true

  const href = hrefForMenu(item)
  if (href) {
    const hrefPath = getPathname(href)
    const routePath = stripSlash(route.path)
    if (hrefPath && hrefPath === routePath) return true
  }

  return false
}
</script>

<!-- 스타일은 전역 레거시 CSS(layout.css 등)의 .lnb.tmn01/.tmn03/.tmn04 규칙을 그대로 사용 -->
